import React, { useEffect, useRef, useState } from "react";
import {
  alpha,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  CheckCircle2,
  Download,
  FileText,
  Info,
  Upload,
  X,
} from "lucide-react";
import { useTrainingCsvUpload } from "@hooks/useTrainingCsvUpload";

interface UploadTrainingModalV2CstProps {
  open: boolean;
  onClose: () => void;
}

const UploadTrainingModalV2Cst: React.FC<UploadTrainingModalV2CstProps> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation(["skillBook-page"]);
  const { t: tUpload } = useTranslation("upload-modal");
  const theme = useTheme();

  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const {
    selectedFile,
    errorMessage,
    isLoading,
    showSuccessMessage,
    reset,
    selectFile,
    confirmUpload,
    downloadTemplate,
  } = useTrainingCsvUpload();

  useEffect(() => {
    if (open) reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleDownloadTemplate = async () => {
    setIsDownloading(true);
    try {
      await downloadTemplate();
    } finally {
      setIsDownloading(false);
    }
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    if (isLoading) return;
    selectFile(e.dataTransfer.files?.[0]);
  };

  return (
    <Dialog
      open={open}
      onClose={isLoading ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.default,
          borderRadius: "16px",
          border: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <DialogTitle sx={{ padding: "20px 24px 8px" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography
            sx={{
              fontSize: "20px",
              fontWeight: 700,
              color: theme.palette.text.primary,
            }}
          >
            {t("upload-training-modal.title")}
          </Typography>
          <IconButton
            onClick={onClose}
            aria-label="close"
            disabled={isLoading}
            sx={{ color: theme.palette.text.primary }}
          >
            <X size={20} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          padding: "12px 24px 8px",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {showSuccessMessage ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1.5,
              py: 4,
            }}
          >
            <CheckCircle2 size={40} color={theme.palette.success.main} />
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: 700,
                color: theme.palette.text.primary,
              }}
            >
              {t("upload-training-modal.success-title")}
            </Typography>
            <Typography
              sx={{
                fontSize: "13px",
                color: theme.palette.text.tertiary,
                textAlign: "center",
              }}
            >
              {t("upload-training-modal.success-message")}
            </Typography>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                padding: "16px",
                borderRadius: "12px",
                backgroundColor: alpha(theme.palette.info.main, 0.12),
                border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
              }}
            >
              <Info
                size={18}
                color={theme.palette.info.main}
                style={{ flexShrink: 0, marginTop: 2 }}
              />
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  flex: 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: theme.palette.text.tertiary,
                  }}
                >
                  {t("upload-training-modal.description")}
                </Typography>
                <Box>
                  <Button
                    variant="outlined"
                    startIcon={
                      isDownloading ? (
                        <CircularProgress size={14} />
                      ) : (
                        <Download size={16} />
                      )
                    }
                    onClick={handleDownloadTemplate}
                    disabled={isDownloading || isLoading}
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      borderRadius: "8px",
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {t("upload-training-modal.download-template-cta")}
                  </Button>
                </Box>
              </Box>
            </Box>

            <Box
              role="button"
              onClick={() => !isLoading && inputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragOver(true);
              }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={onDrop}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                padding: "32px 24px",
                borderRadius: "12px",
                cursor: isLoading ? "default" : "pointer",
                border: `1.5px dashed ${
                  isDragOver
                    ? theme.palette.primary.main
                    : theme.palette.divider
                }`,
                backgroundColor: isDragOver
                  ? alpha(theme.palette.primary.main, 0.06)
                  : "transparent",
                transition: "border-color .15s, background-color .15s",
              }}
            >
              {selectedFile ? (
                <>
                  <FileText size={26} color={theme.palette.primary.main} />
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 700,
                      color: theme.palette.text.primary,
                      wordBreak: "break-all",
                      textAlign: "center",
                    }}
                  >
                    {selectedFile.name}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "13px",
                      color: theme.palette.text.tertiary,
                      textAlign: "center",
                    }}
                  >
                    {tUpload("dropzone-text")}
                  </Typography>
                </>
              ) : (
                <>
                  <Upload size={26} color={theme.palette.primary.main} />
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 700,
                      color: theme.palette.primary.main,
                    }}
                  >
                    {tUpload("dropzone-title")}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "13px",
                      color: theme.palette.text.tertiary,
                      textAlign: "center",
                    }}
                  >
                    {tUpload("dropzone-text")}
                  </Typography>
                </>
              )}
              <input
                ref={inputRef}
                type="file"
                accept=".csv,.CSV"
                hidden
                onChange={(e) => {
                  selectFile(e.target.files?.[0]);
                  e.target.value = "";
                }}
              />
            </Box>

            {errorMessage && (
              <Box
                sx={{
                  padding: "12px 16px",
                  borderRadius: "12px",
                  backgroundColor: alpha(theme.palette.error.main, 0.12),
                  border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                  fontSize: "13px",
                  color: theme.palette.text.primary,
                }}
                dangerouslySetInnerHTML={{ __html: errorMessage }}
              />
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ padding: "12px 24px 20px" }}>
        <Box sx={{ flex: 1 }} />
        {showSuccessMessage ? (
          <Button
            variant="contained"
            onClick={onClose}
            sx={{ textTransform: "none", borderRadius: "8px", fontWeight: 700 }}
          >
            {t("finish")}
          </Button>
        ) : (
          <>
            <Button
              variant="text"
              onClick={onClose}
              disabled={isLoading}
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                fontWeight: 600,
                color: theme.palette.text.primary,
              }}
            >
              {t("upload-training-modal.cancel-cta")}
            </Button>
            <Button
              variant="contained"
              onClick={confirmUpload}
              disabled={!selectedFile || isLoading}
              startIcon={isLoading ? <CircularProgress size={14} /> : undefined}
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                fontWeight: 700,
              }}
            >
              {t("upload-training-modal.confirm-cta")}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default UploadTrainingModalV2Cst;
